/**
 * Timezone utilities for handling time conversions between UTC and local time
 * Properly handles both date and time components during conversion.
 *
 * No Luxon import: every caller of the former `formatTimeWithLuxon` uses the
 * default `'HH:mm'` format, which is trivially produced with native Date /
 * string formatting. Pulling Luxon (~26 KiB minified) onto every page that
 * transitively touched this module — including the public activity detail
 * page via the auth-gated booking history dep chain — was the dominant
 * source of Lighthouse's "Reduce unused JavaScript" finding.
 */

/**
 * Convert UTC time slot to local time, considering the specific date
 * @param utcStartTime Minutes since midnight in UTC
 * @param utcEndTime Minutes since midnight in UTC
 * @param utcDate The UTC date for the time slot
 * @returns Object with local time and potentially adjusted date
 */
export function convertUtcTimeSlotToLocal(
  utcStartTime: number,
  utcEndTime: number,
  utcDate: Date
): {
  startTime: number;
  endTime: number;
  localDate: Date;
  dateShifted: boolean;
} {
  // Handle endTime > 1440 (represents next day after midnight crossing)
  // If endTime > 1440, it means the end is on the next day
  const actualUtcEndTime = utcEndTime > 1440 ? utcEndTime - 1440 : utcEndTime;
  const endDateOffset = utcEndTime > 1440 ? 1 : 0;

  // Create UTC datetime for start time (use UTC date components so the slot is on the intended UTC date)
  const utcStartHours = Math.floor(utcStartTime / 60);
  const utcStartMinutes = utcStartTime % 60;

  const utcStartDateTime = new Date(
    Date.UTC(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcStartHours,
      utcStartMinutes,
      0,
      0
    )
  );

  // Create UTC datetime for end time (may be on next day)
  const utcEndHours = Math.floor(actualUtcEndTime / 60);
  const utcEndMinutes = actualUtcEndTime % 60;

  const utcEndDateTime = new Date(
    Date.UTC(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate() + endDateOffset,
      utcEndHours,
      utcEndMinutes,
      0,
      0
    )
  );

  // Convert to local time
  const localStartDateTime = new Date(utcStartDateTime);
  const localEndDateTime = new Date(utcEndDateTime);

  // Extract local date and time components
  const localDate = new Date(
    localStartDateTime.getFullYear(),
    localStartDateTime.getMonth(),
    localStartDateTime.getDate()
  );

  // Calculate minutes since midnight for local time
  const localStartMinutes = localStartDateTime.getHours() * 60 + localStartDateTime.getMinutes();
  const localEndMinutes = localEndDateTime.getHours() * 60 + localEndDateTime.getMinutes();

  // Check if date shifted due to timezone conversion (compare UTC calendar day vs local calendar day)
  const utcDayStr = `${utcDate.getUTCFullYear()}-${utcDate.getUTCMonth()}-${utcDate.getUTCDate()}`;
  const localDayStr = `${localStartDateTime.getFullYear()}-${localStartDateTime.getMonth()}-${localStartDateTime.getDate()}`;
  const dateShifted = utcDayStr !== localDayStr;

  return {
    startTime: localStartMinutes,
    endTime: localEndMinutes,
    localDate,
    dateShifted
  };
}

/**
 * Convert local time slot to UTC time, considering the specific date
 * @param localStartTime Minutes since midnight in local time
 * @param localEndTime Minutes since midnight in local time
 * @param localDate The local date for the time slot
 * @returns Object with UTC time and potentially adjusted date
 */
export function convertLocalTimeSlotToUtc(
  localStartTime: number,
  localEndTime: number,
  localDate: Date
): {
  startTime: number;
  endTime: number;
  utcDate: Date;
  dateShifted: boolean;
} {
  // Create local datetime for start time
  const localStartHours = Math.floor(localStartTime / 60);
  const localStartMinutes = localStartTime % 60;

  const localStartDateTime = new Date(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    localStartHours,
    localStartMinutes,
    0,
    0
  );

  // Create local datetime for end time
  const localEndHours = Math.floor(localEndTime / 60);
  const localEndMinutes = localEndTime % 60;

  const localEndDateTime = new Date(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    localEndHours,
    localEndMinutes,
    0,
    0
  );

  // Convert to UTC
  const utcStartDateTime = new Date(localStartDateTime.getTime());
  const utcEndDateTime = new Date(localEndDateTime.getTime());

  // Extract UTC date and time components
  const utcDate = new Date(
    Date.UTC(utcStartDateTime.getUTCFullYear(), utcStartDateTime.getUTCMonth(), utcStartDateTime.getUTCDate())
  );

  // Calculate minutes since midnight for UTC time
  const utcStartMinutes = utcStartDateTime.getUTCHours() * 60 + utcStartDateTime.getUTCMinutes();
  let utcEndMinutes = utcEndDateTime.getUTCHours() * 60 + utcEndDateTime.getUTCMinutes();

  // Handle midnight crossing in UTC: if end time is before start time, it means the session
  // crosses midnight in UTC (even if it doesn't in local time). Add 1440 to represent next day.
  if (utcEndMinutes < utcStartMinutes) {
    utcEndMinutes = utcEndMinutes + 1440;
  }

  // Check if date shifted due to timezone conversion
  const originalDateString = localDate.toDateString();
  const utcDateString = utcDate.toDateString();
  const dateShifted = originalDateString !== utcDateString;

  return {
    startTime: utcStartMinutes,
    endTime: utcEndMinutes,
    utcDate,
    dateShifted
  };
}

/**
 * Convert time in minutes to formatted time string (HH:MM)
 * @param minutes Minutes since midnight
 * @returns Formatted time string
 */
export function formatTimeFromMinutes(minutes: number): string {
  // Handle case where minutes might be negative (due to timezone conversion)
  // or exceed 24 hours (also due to timezone conversion)
  let adjustedMinutes = minutes;

  // Normalize to 0-1439 range (24 hours)
  if (adjustedMinutes < 0) {
    adjustedMinutes = ((adjustedMinutes % 1440) + 1440) % 1440;
  } else if (adjustedMinutes >= 1440) {
    adjustedMinutes = adjustedMinutes % 1440;
  }

  const hours = Math.floor(adjustedMinutes / 60);
  const mins = adjustedMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Get the user's timezone identifier
 * @returns Timezone identifier (e.g., 'America/New_York', 'Europe/London')
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Convert a session's time slots from UTC to local time
 * @param session Session with UTC time slots
 * @param targetDate The date for which to convert the times
 * @returns Session with local time slots
 */
export function convertSessionTimeSlots(
  session: {
    timeSlots: Array<{
      startTime: number;
      endTime: number;
      price: number;
    }>;
    startDate: string; // ISO format YYYY-MM-DD
  },
  targetDate?: Date
): {
  timeSlots: Array<{
    startTime: number;
    endTime: number;
    price: number;
  }>;
  localDate: Date;
  hasDateShifts: boolean;
} {
  // Parse the session start date or use target date
  const sessionDate = targetDate || new Date(session.startDate + 'T00:00:00.000Z');

  let hasDateShifts = false;
  const convertedTimeSlots = session.timeSlots.map((slot) => {
    const converted = convertUtcTimeSlotToLocal(slot.startTime, slot.endTime, sessionDate);

    if (converted.dateShifted) {
      hasDateShifts = true;
    }

    return {
      startTime: converted.startTime,
      endTime: converted.endTime,
      price: slot.price
    };
  });

  return {
    timeSlots: convertedTimeSlots,
    localDate: sessionDate,
    hasDateShifts
  };
}

/**
 * Format a date and time for display in local timezone
 * @param utcMinutes Minutes since midnight in UTC
 * @param utcDate UTC date
 * @returns Formatted local date and time string
 */
export function formatLocalDateTime(utcMinutes: number, utcDate: Date): string {
  const utcHours = Math.floor(utcMinutes / 60);
  const utcMins = utcMinutes % 60;

  const utcDateTime = new Date(
    Date.UTC(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate(), utcHours, utcMins, 0, 0)
  );

  // Convert to local time and format
  return utcDateTime.toLocaleString();
}

/**
 * Format a number of minutes-since-midnight as a clock time string.
 *
 * Kept under the legacy name `formatTimeWithLuxon` so callers don't need to be
 * updated — but the implementation is now Luxon-free. Every current caller
 * uses the default `'HH:mm'` format; the optional second arg supports ONLY
 * the Luxon-style `H`, `HH`, `m`, `mm` tokens that callers actually use today.
 *
 * Any other token (`a`, `EEEE`, `LLLL`, …) is returned verbatim as a literal
 * character — there is no Intl fallback. If you need richer formatting, add
 * it explicitly here or build an `Intl.DateTimeFormat` at the call site.
 */
export function formatTimeWithLuxon(minutes: number, format: string = 'HH:mm'): string {
  let adjustedMinutes = minutes;

  if (adjustedMinutes < 0) {
    adjustedMinutes = ((adjustedMinutes % 1440) + 1440) % 1440;
  } else if (adjustedMinutes >= 1440) {
    adjustedMinutes = adjustedMinutes % 1440;
  }

  const hours = Math.floor(adjustedMinutes / 60);
  const mins = adjustedMinutes % 60;

  // Hot path: HH:mm (every caller as of this commit).
  if (format === 'HH:mm') {
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Cold path: only the four tokens below are substituted. Any other letters
  // in the format string are passed through unchanged.
  return format
    .replace(/HH/g, hours.toString().padStart(2, '0'))
    .replace(/H/g, hours.toString())
    .replace(/mm/g, mins.toString().padStart(2, '0'))
    .replace(/m/g, mins.toString());
}

/**
 * Convert API availability data to display-ready time slots with timezone conversion
 * This function handles the complex conversion from UTC API data to local display format
 * @param availabilityData Raw API data for a specific day, optionally enriched with sessionName
 * @param selectedDate The date for which to convert the times
 * @returns Array of time slots ready for UI display
 */
export function convertApiSlotsToDisplaySlots(
  availabilityData: Array<{
    sessionId: string;
    sessionName?: string;
    timeSlots: Array<{
      startTime: number;
      endTime: number;
      price: number;
      availableNumberOfPeople: number;
      acceptsCash?: boolean;
      acceptsOnline?: boolean;
    }>;
  }>,
  selectedDate: Date
): Array<{
  id: string;
  time: string;
  price: string;
  available: boolean;
  metadata: {
    utcStartTime: number;
    utcEndTime: number;
    sessionId: string;
    sessionName?: string;
    acceptsCash?: boolean;
    acceptsOnline?: boolean;
  };
}> {
  const slots: Array<{
    id: string;
    time: string;
    price: string;
    available: boolean;
    metadata: {
      utcStartTime: number;
      utcEndTime: number;
      sessionId: string;
      sessionName?: string;
      acceptsCash?: boolean;
      acceptsOnline?: boolean;
    };
  }> = [];

  availabilityData.forEach((sessionAvailability) => {
    sessionAvailability.timeSlots.forEach((apiSlot) => {
      // Convert UTC time slot to local time for display
      const localConversion = convertUtcTimeSlotToLocal(apiSlot.startTime, apiSlot.endTime, selectedDate);

      // Use enhanced Luxon formatting for better time display
      const localStartTime = formatTimeWithLuxon(localConversion.startTime);
      const localEndTime = formatTimeWithLuxon(localConversion.endTime);
      const displayTime = `${localStartTime} - ${localEndTime}`;

      slots.push({
        id: `${sessionAvailability.sessionId}-slot-${apiSlot.startTime}-${apiSlot.endTime}`,
        time: displayTime,
        price: apiSlot.price.toString(),
        available: apiSlot.availableNumberOfPeople > 0,
        // Keep UTC values as metadata for form submission
        metadata: {
          utcStartTime: apiSlot.startTime,
          utcEndTime: apiSlot.endTime,
          sessionId: sessionAvailability.sessionId,
          ...(sessionAvailability.sessionName !== undefined && { sessionName: sessionAvailability.sessionName }),
          ...(apiSlot.acceptsCash !== undefined && { acceptsCash: apiSlot.acceptsCash }),
          ...(apiSlot.acceptsOnline !== undefined && { acceptsOnline: apiSlot.acceptsOnline })
        }
      });
    });
  });

  return slots;
}
